import { useState } from "react";
import { CheckCircle2, XCircle, Copy, Save, PlugZap } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDateTime, fmtDate } from "@/lib/api";
import { Card } from "@/components/Ui";
import { Button } from "@/components/ui/button";
import { inputClass } from "@/components/FieldInput";

const SRC = { area: "salvato in quest'area", secrets: "da Secrets di pubblicazione", default: "valore predefinito" };

function Field({ f, pid, value, onChange, onClear }) {
  const id = `integ-${pid}-${f.key}`;
  const multiline = f.key === "private_key_b64";
  const Tag = multiline ? "textarea" : "input";
  return (
    <div>
      <label htmlFor={id} className="text-xs font-medium text-slate-600">{f.label}</label>
      <Tag id={id} data-testid={id} type={f.kind === "secret" ? "password" : "text"} autoComplete="off" spellCheck={false}
        className={`${inputClass} mt-1 font-mono text-xs ${multiline ? "h-20 py-2" : ""}`} value={value}
        placeholder={f.is_set ? f.display : "non impostato"} onChange={(e) => onChange(e.target.value)} />
      <p className="text-[11px] text-slate-400 mt-1 flex items-center gap-2">
        {f.is_set ? <span>Attuale: {f.display} · {SRC[f.source]}{f.saved_at ? ` il ${fmtDate(f.saved_at)}` : ""}</span> : <span>Non impostato</span>}
        {f.source === "area" && (
          <button type="button" className="text-red-600 hover:underline" onClick={onClear} data-testid={`${id}-clear`}>rimuovi</button>
        )}
      </p>
    </div>
  );
}

export function IntegrationCard({ item, onChanged }) {
  const [vals, setVals] = useState({});
  const [busy, setBusy] = useState(false);
  const pid = item.provider;
  const t = item.last_test;

  const run = async (fn) => {
    setBusy(true);
    try { await fn(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const save = (clear = []) => run(async () => {
    await api.put(`/integrations/${pid}`, { values: vals, clear });
    setVals({});
    toast.success("Credenziali salvate e cifrate");
    onChanged();
  });
  const test = () => run(async () => {
    const { data } = await api.post(`/integrations/${pid}/test`);
    data.ok ? toast.success(data.message) : toast.error(data.message);
    onChanged();
  });
  const dirty = Object.values(vals).some((v) => v.trim());

  return (
    <Card className="p-5" data-testid={`integration-card-${pid}`}>
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <p className="font-heading font-semibold text-slate-900">{item.label}</p>
          <p className="text-[11px] text-slate-500 mt-0.5">
            {item.updated_at ? `Ultima modifica ${fmtDateTime(item.updated_at)} · ${item.updated_by}` : "Mai modificato da quest'area"}
          </p>
        </div>
        {t && (
          <span className={`text-[11px] flex items-center gap-1 max-w-[50%] text-right ${t.ok ? "text-emerald-700" : "text-red-700"}`} data-testid={`integration-status-${pid}`}>
            {t.ok ? <CheckCircle2 className="h-3.5 w-3.5 shrink-0" /> : <XCircle className="h-3.5 w-3.5 shrink-0" />}
            {t.message} · {fmtDateTime(t.at)}
          </span>
        )}
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        {item.fields.map((f) => (
          <Field key={f.key} f={f} pid={pid} value={vals[f.key] || ""} onChange={(v) => setVals((s) => ({ ...s, [f.key]: v }))}
            onClear={() => window.confirm(`Rimuovere “${f.label}”? Verrà usato l'eventuale valore dei Secrets.`) && save([f.key])} />
        ))}
      </div>
      {item.hints.length > 0 && (
        <div className="mt-4 rounded-md bg-slate-50 p-3 space-y-1.5">
          {item.hints.map((h) => (
            <p key={h.label} className="text-[11px] text-slate-600 flex items-center gap-2 flex-wrap">
              <span className="font-medium">{h.label}:</span><code className="text-slate-800 break-all">{h.value}</code>
              <button type="button" onClick={() => { navigator.clipboard.writeText(h.value); toast.success("Copiato"); }} aria-label="Copia"
                data-testid={`integration-hint-copy-${pid}-${h.label.replace(/\W+/g, "-").toLowerCase()}`}><Copy className="h-3 w-3 text-slate-400 hover:text-slate-700" /></button>
            </p>
          ))}
        </div>
      )}
      <div className="flex gap-2 mt-4 justify-end">
        <Button variant="outline" disabled={busy} onClick={test} data-testid={`integration-test-${pid}`}><PlugZap className="h-4 w-4 mr-1" />Verifica connessione</Button>
        <Button disabled={busy || !dirty} onClick={() => save()} className="bg-[#0A2540] hover:bg-[#031B33] text-white" data-testid={`integration-save-${pid}`}>
          <Save className="h-4 w-4 mr-1" />Salva
        </Button>
      </div>
    </Card>
  );
}
